import { Injectable } from '@angular/core';
import { FUSE_VERSION } from '@fuse/version';
import { environment } from 'app/environments/environment';
import { LoggerService } from './logger.service';

/**
 * CacheBusterService
 *
 * Detects when the frontend version has changed since the last time the user
 * opened the app (i.e. after a new deployment) and performs a one-time cache
 * cleanup + page reload so that stale assets and localStorage data are
 * replaced by the new release.
 *
 * Flow (runs once on every app startup via APP_INITIALIZER):
 *  1. Read the last-seen version from localStorage.
 *  2. Compare it with the current environment.version used by the UI.
 *  3. If they differ → write the new version → clear stale data → reload.
 *  4. On the reloaded page the versions match → nothing happens → normal boot.
 *
 * Cost when versions match: one localStorage.getItem() call – negligible.
 */
@Injectable({ providedIn: 'root' })
export class CacheBusterService {
  private static readonly VERSION_KEY = 'gp_app_version';

  constructor(private readonly logger: LoggerService) {}

  /**
   * Called by the APP_INITIALIZER factory.
   * Returns a Promise so Angular waits for the potential reload to begin.
   */
  checkAndClearIfVersionChanged(): Promise<void> {
    const storedVersion = localStorage.getItem(CacheBusterService.VERSION_KEY);
    const currentVersion = this.resolveCurrentVersion();

    // Always emit a plain console line for troubleshooting, even if LoggerService
    // suppresses info logs in production.
    console.info('[CacheBusterService] version check', {
      storedVersion,
      currentVersion,
      environmentVersion: environment.version,
      fuseVersion: FUSE_VERSION,
    });

    if (storedVersion === currentVersion) {
      // Same version – nothing to do.
      return Promise.resolve();
    }
    this.logger.info(
      'CacheBusterService',
      `Version changed: ${storedVersion ?? 'none'} → ${currentVersion}. Clearing stale cache…`
    );

    // Persist the new version BEFORE clearing so the reload sees the correct value.
    localStorage.setItem(CacheBusterService.VERSION_KEY, currentVersion);

    // Clear all remaining localStorage entries (stale app data, user settings,
    // cached API payloads, etc.).  MSAL tokens are stored in sessionStorage
    // (BrowserCacheLocation.SessionStorage) and are unaffected.
    const keysToRemove = Object.keys(localStorage).filter(
      (k) => k !== CacheBusterService.VERSION_KEY
    );
    keysToRemove.forEach((k) => localStorage.removeItem(k));

    // Clear sessionStorage (MSAL will silently re-acquire tokens via SSO).
    sessionStorage.clear();

    // Clear any Cache API entries (defensive – no service worker is currently
    // registered, but covers future additions).
    const cacheClean = typeof caches !== 'undefined'
      ? caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n))))
      : Promise.resolve();

    return cacheClean.then(() => {
      this.logger.info('CacheBusterService', 'Cache cleared. Reloading page…');
      window.location.reload();
      // Return a never-resolving Promise so Angular's bootstrap waits while the
      // browser begins the reload.  Without this the app would briefly continue
      // bootstrapping with stale providers before the reload completes.
      return new Promise<void>(() => {});
    });
  }

  private resolveCurrentVersion(): string {
    const envVersion = String(environment.version ?? '').trim();
    if (envVersion) {
      return envVersion;
    }

    // Fallback for safety in case environment.version is accidentally removed.
    return FUSE_VERSION;
  }
}
